import { ReactNode, useCallback } from 'react';

import {
  GetTaskFileUploadUrlRequest,
  useGetTaskFileDownloadUrl,
  useGetTaskFileUploadUrl,
} from 'services/repositories/tasks/tasks';
import { uploadFileToS3WithPresignedUrl } from 'support/helpers/fileUpload/fileUpload';
import {
  FileUploadFn,
  GetFileDownloadUrlFn,
} from 'components/SchemableForm/contexts/SchemableFormFileUploadContext/SchemableFormFileUploadContext';
import { SchemableFormFileUploadContextProvider } from 'components/SchemableForm/contexts/SchemableFormFileUploadContext/SchemableFormFileUploadContextProvider';

export const FileUploadWrapper = ({
  relationshipId,
  taskId,
  children,
}: {
  relationshipId: string | undefined;
  taskId: string;
  children: ReactNode;
}) => {
  const { mutateAsync: getUploadUrl } = useGetTaskFileUploadUrl();
  const { mutateAsync: getDownloadUrl } = useGetTaskFileDownloadUrl();

  const uploadFile: FileUploadFn = useCallback(
    async (file) => {
      if (!relationshipId || !taskId) {
        throw new Error('File upload is not available');
      }
      const data = await getUploadUrl({
        relationshipId,
        taskId,
        payload: { file_name: file.name, mime_type: file.type as GetTaskFileUploadUrlRequest['mime_type'] },
      });
      if (!data?.url) {
        throw new Error('No upload URL returned');
      }
      await uploadFileToS3WithPresignedUrl(file, data.url);
      return { fileName: data.file_name };
    },
    [getUploadUrl, relationshipId, taskId],
  );

  const getFileDownloadUrl: GetFileDownloadUrlFn = useCallback(
    async (fileName) => {
      if (!relationshipId || !taskId) {
        throw new Error('File download is not available');
      }
      const data = await getDownloadUrl({ relationshipId, taskId, name: fileName });
      if (!data?.url) {
        throw new Error('No download URL returned');
      }
      return { url: data.url };
    },
    [getDownloadUrl, relationshipId, taskId],
  );

  if (!relationshipId || !taskId) {
    return <>{children}</>;
  }

  return (
    <SchemableFormFileUploadContextProvider uploadFile={uploadFile} getFileDownloadUrl={getFileDownloadUrl}>
      {children}
    </SchemableFormFileUploadContextProvider>
  );
};
